"use client";

import { useState } from "react";
import { useMarketingText } from "@/lib/i18n/marketingClient";

type Row = {
  id: string;
  kind: string;
  name: string;
  detail: string;
  status: "suggested" | "approved";
};

export const STORY_BIBLE_GLIMPSE_ROWS = {
  default: [
    {
      id: "1",
      kind: "Character",
      name: "Elara Voss",
      detail: "Grey eyes in ch. 2; carries the brass locket",
      status: "suggested" as const,
    },
    {
      id: "2",
      kind: "Place",
      name: "The Zinc Roof",
      detail: "7th arrondissement rooftop used in the opening",
      status: "suggested" as const,
    },
    {
      id: "3",
      kind: "Rule",
      name: "Burner phones go dark after one ping",
      detail: "Stated in ch. 1; referenced again in ch. 4",
      status: "approved" as const,
    },
  ] satisfies Row[],
  characters: [
    {
      id: "c1",
      kind: "Character",
      name: "Elara Voss",
      detail: "Aliases: Elena, Captain. Grey eyes in ch. 2; brass locket",
      status: "suggested" as const,
    },
    {
      id: "c2",
      kind: "Character",
      name: "Marek Quinn",
      detail: "Brother to Elara in ch. 3; estranged by ch. 18",
      status: "suggested" as const,
    },
    {
      id: "c3",
      kind: "Character",
      name: "Dr. Imani Okonkwo",
      detail: "Left-handed surgeon; scar on right forearm (ch. 5)",
      status: "approved" as const,
    },
  ] satisfies Row[],
};

export function StoryBibleGlimpse({
  variant = "default",
}: {
  variant?: keyof typeof STORY_BIBLE_GLIMPSE_ROWS;
}) {
  const [rows, setRows] = useState<Row[]>(() => [...STORY_BIBLE_GLIMPSE_ROWS[variant]]);
  const localize = useMarketingText();

  return (
    <figure
      aria-label={
        variant === "characters"
          ? localize("Novilot suggesting character story bible entries from a manuscript for you to approve.")
          : localize("Novilot suggesting story bible entries from a manuscript for you to approve.")
      }
      className="w-full max-w-xl mx-auto lg:max-w-none"
    >
      <div className="rounded-2xl border border-border/80 bg-muted/20 p-5 shadow-sm">
        <div className="rounded-xl border border-border bg-background overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">
                {variant === "characters"
                  ? localize("Character suggestions")
                  : localize("Story Bible suggestions")}
              </p>
              <p className="text-xs text-muted-foreground">
                {localize("From your manuscript. You approve what goes in.")}
              </p>
            </div>
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground shrink-0">
              {localize("Suggest then approve")}
            </span>
          </div>
          <ul className="divide-y divide-border">
            {rows.map((row) => (
              <li
                key={row.id}
                className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
              >
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {localize(row.kind)}
                  </p>
                  <p className="font-medium">{row.kind === "Rule" ? localize(row.name) : row.name}</p>
                  <p className="text-sm text-muted-foreground">{localize(row.detail)}</p>
                </div>
                {row.status === "approved" ? (
                  <span className="text-xs font-medium text-accent shrink-0">{localize("Approved")}</span>
                ) : (
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      className="text-xs px-3 py-1.5 rounded-lg bg-accent text-accent-foreground"
                      onClick={() =>
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, status: "approved" } : r,
                          ),
                        )
                      }
                    >
                      {localize("Approve")}
                    </button>
                    <button
                      type="button"
                      className="text-xs px-3 py-1.5 rounded-lg border border-border"
                      onClick={() =>
                        setRows((prev) => prev.filter((r) => r.id !== row.id))
                      }
                    >
                      {localize("Skip")}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </figure>
  );
}
